import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  BookOpen, Plus, Loader2, CheckCircle, Pencil, Trash2,
  RefreshCw, Upload, Download, Sparkles, Lock, Unlock,
  FolderOpen, ArrowLeft, ChevronRight, MoreVertical, ClipboardPaste
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminQuestionsPage = () => {
  // Navigation: folders → categories → builder
  const [view, setView] = useState('folders'); // folders | categories | builder
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [categories, setCategories] = useState([]);
  const [inheritedAgeGroup, setInheritedAgeGroup] = useState('Unassigned');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create category form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    questionCount: 10,
    timeLimit: 7
  });

  // Builder state
  const [activeCategory, setActiveCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [aiInstructions, setAiInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [confirmFinalize, setConfirmFinalize] = useState(false);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');

  // Folder menu state
  const [folderMenuOpen, setFolderMenuOpen] = useState(null);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Create-folder state (manual; standalone — independent of any competition)
  const [showNewFolderForm, setShowNewFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  // Category menu state
  const [catMenuOpen, setCatMenuOpen] = useState(null);
  const [renamingCat, setRenamingCat] = useState(null);
  const [catRenameValue, setCatRenameValue] = useState('');

  // Paste questions state
  const [showPasteBox, setShowPasteBox] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [parseError, setParseError] = useState('');

  useEffect(() => {
    loadFolders();
    loadTemplates();
  }, []);

  // Close menus on outside click
  useEffect(() => {
    if (!folderMenuOpen && !catMenuOpen) return;
    const handler = () => { setFolderMenuOpen(null); setCatMenuOpen(null); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [folderMenuOpen, catMenuOpen]);

  const loadFolders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/question-folders`, { withCredentials: true });
      setFolders(res.data.folders || []);
    } catch (e) {
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const openFolder = async (folder) => {
    setActiveFolder(folder);
    setView('categories');
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/question-folders/${folder.id}/categories`, { withCredentials: true });
      setCategories(res.data.categories || []);
      setInheritedAgeGroup(res.data.inherited_age_group || 'Unassigned');
    } catch (e) {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const backToFolders = () => {
    setView('folders');
    setActiveFolder(null);
    setCategories([]);
    loadFolders();
  };

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/quiz-templates`, { withCredentials: true });
      setTemplates(res.data.templates || []);
    } catch (e) {
      setTemplates([]);
    }
  };

  const createCategory = async () => {
    if (!newCategory.name.trim()) {
      setError('Category name is required');
      return;
    }
    if (!activeFolder) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/admin/quiz-categories`, {
        ...newCategory,
        folder_id: activeFolder.id
      }, { withCredentials: true });
      setNewCategory({ name: '', questionCount: 10, timeLimit: 7 });
      setShowCreateForm(false);
      setSuccess('Category created!');
      openFolder(activeFolder);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const openBuilder = async (category) => {
    setActiveCategory(category);
    setView('builder');
    try {
      const res = await axios.get(`${API_URL}/api/admin/quiz-categories/${category.id}/questions`, { withCredentials: true });
      const loaded = res.data.questions || [];
      setQuestions(loaded);
      setAiInstructions(category.aiInstructions || '');
      setIsReadOnly(loaded.length > 0 || category.status === 'finalized');
    } catch (e) {
      setQuestions([]);
      setIsReadOnly(false);
    }
  };

  const generateQuestions = async () => {
    if (!aiInstructions.trim()) {
      setError('Please provide AI instructions');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/admin/quiz-categories/${activeCategory.id}/generate`, {
        instructions: aiInstructions,
        count: activeCategory.questionCount
      }, { withCredentials: true });
      setQuestions(res.data.questions || []);
      setSuccess(`Generated ${res.data.questions?.length || 0} questions!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const regenerateQuestion = async (index) => {
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/admin/quiz-categories/${activeCategory.id}/regenerate`, {
        index,
        instructions: aiInstructions
      }, { withCredentials: true });
      const updated = [...questions];
      updated[index] = res.data.question;
      setQuestions(updated);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to regenerate');
    }
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const deleteQuestion = (index) => {
    setQuestions(q => q.filter((_, i) => i !== index));
  };

  const addBlankQuestion = () => {
    setQuestions(q => [...q, {
      question_text: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: '',
      correct_option: 'A'
    }]);
  };

  const saveQuestions = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.put(`${API_URL}/api/admin/quiz-categories/${activeCategory.id}/questions`, {
        questions,
        aiInstructions
      }, { withCredentials: true });
      setSuccess('Questions saved!');
      setIsReadOnly(true);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const finalizeCategory = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/admin/quiz-categories/${activeCategory.id}/finalize`, {}, { withCredentials: true });
      setActiveCategory(prev => ({ ...prev, status: 'finalized' }));
      setIsReadOnly(true);
      setConfirmFinalize(false);
      setSuccess('Category finalized!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to finalize');
    } finally {
      setLoading(false);
    }
  };

  const unlockCategory = async (catId, opts = {}) => {
    try {
      await axios.post(`${API_URL}/api/admin/quiz-categories/${catId}/unlock`, {}, { withCredentials: true });
      if (opts.stayInBuilder) {
        setActiveCategory(prev => ({ ...prev, status: 'draft' }));
        setIsReadOnly(false);
        setSuccess('Category unlocked! You can now edit.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        openFolder(activeFolder);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to unlock');
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim() || !aiInstructions.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/quiz-templates`, {
        name: templateName,
        instructions: aiInstructions
      }, { withCredentials: true });
      setTemplateName('');
      setSuccess('Template saved!');
      loadTemplates();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save template');
    }
  };

  const loadTemplate = (template) => {
    setAiInstructions(template.instructions);
  };

  const renameFolder = async (folderId) => {
    if (!renameValue.trim()) return;
    setError('');
    try {
      await axios.put(`${API_URL}/api/admin/question-folders/${folderId}/rename`, { name: renameValue.trim() }, { withCredentials: true });
      setRenamingFolder(null);
      setRenameValue('');
      setSuccess('Folder renamed!');
      loadFolders();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to rename folder');
    }
  };

  const deleteFolder = async (folder) => {
    if (!window.confirm('Are you sure you want to delete this folder and all its categories? This cannot be undone.')) return;
    setError('');
    try {
      await axios.delete(`${API_URL}/api/admin/question-folders/${folder.id}`, { withCredentials: true });
      setSuccess('Folder deleted!');
      loadFolders();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const createFolder = async () => {
    const name = (newFolderName || '').trim();
    if (!name) return;
    setError('');
    setCreatingFolder(true);
    try {
      await axios.post(`${API_URL}/api/admin/question-folders`, { name }, { withCredentials: true });
      setSuccess(`Folder "${name}" created!`);
      setNewFolderName('');
      setShowNewFolderForm(false);
      loadFolders();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const renameCategory = async (catId) => {
    if (!catRenameValue.trim()) return;
    setError('');
    try {
      await axios.put(`${API_URL}/api/admin/quiz-categories/${catId}/rename`, { name: catRenameValue.trim() }, { withCredentials: true });
      setRenamingCat(null);
      setCatRenameValue('');
      setSuccess('Category renamed!');
      openFolder(activeFolder);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to rename category');
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Are you sure you want to delete "${cat.name}" and all its questions? This cannot be undone.`)) return;
    setError('');
    try {
      await axios.delete(`${API_URL}/api/admin/quiz-categories/${cat.id}`, { withCredentials: true });
      setSuccess('Category deleted!');
      openFolder(activeFolder);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  const parseAndLoadQuestions = () => {
    setParseError('');
    const text = pasteText.trim();
    if (!text) { setParseError('Paste your questions first.'); return; }

    const parsed = [];

    // Strategy 1: Try splitting by numbered questions (1. / 2. / Q1 etc)
    // Strategy 2: Try splitting by blank lines
    // Strategy 3: Try line-by-line with "question - opt, opt, opt, opt (answer)" format

    // Normalize text
    const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // Try to split into question blocks
    let blocks = [];

    // Pattern: numbered questions split (1. / 1) / Q1 / Question 1)
    const numberedSplit = normalized.split(/\n(?=\s*(?:\d+[\.\)\-:]\s|Q\d+[\.\)\-:]\s|Question\s+\d+))/i);
    if (numberedSplit.length > 1) {
      blocks = numberedSplit.filter(b => b.trim());
    } else {
      // Fall back to blank line split
      blocks = normalized.split(/\n\s*\n/).filter(b => b.trim());
    }

    // If still just one block, try line-by-line (each line = one question with inline options)
    if (blocks.length <= 1 && normalized.includes('\n')) {
      const lines = normalized.split('\n').map(l => l.trim()).filter(Boolean);
      // Check if lines look like inline format: "question - opt1, opt2, opt3, opt4 (answer)"
      const inlineResults = tryInlineParse(lines);
      if (inlineResults.length > 0) {
        parsed.push(...inlineResults);
      }
    }

    if (parsed.length === 0) {
      // Parse each block
      for (const block of blocks) {
        const q = parseBlock(block.trim());
        if (q) parsed.push(q);
      }
    }

    if (parsed.length === 0) {
      setParseError(`Could not parse any questions. Try formats like:\n• Numbered with A) B) C) D) and Correct: X\n• "Question - opt1, opt2, opt3, opt4 (correct answer)"\n• Comma/semicolon separated options on lines after question`);
      return;
    }

    setQuestions(prev => [...prev, ...parsed]);
    setShowPasteBox(false);
    setPasteText('');
    setSuccess(`${parsed.length} question${parsed.length > 1 ? 's' : ''} loaded! Review and save.`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const parseBlock = (block) => {
    const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return null;

    // Extract question text (first line, strip number/prefix)
    let questionText = lines[0]
      .replace(/^\d+[\.\)\-:]\s*/, '')
      .replace(/^Q\d+[\.\)\-:]\s*/i, '')
      .replace(/^Question\s+\d+[\.\)\-:]\s*/i, '')
      .trim();

    let optA = '', optB = '', optC = '', optD = '', correct = '';

    // Strategy A: Look for labeled options (A. / A) / a: / (A) etc)
    const optionLines = [];
    const otherLines = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const optMatch = line.match(/^[\(\[]?([A-Da-d])[\.\)\]\-:]\s*(.+)/);
      const correctMatch = line.match(/^(?:Correct|Answer|Ans|Correct Answer|Right)[\s\-:]+(.+)/i);
      if (optMatch) {
        optionLines.push({ letter: optMatch[1].toUpperCase(), value: optMatch[2].trim() });
      } else if (correctMatch) {
        const ans = correctMatch[1].trim();
        // Could be letter (A) or full text (Jupiter)
        if (/^[A-Da-d]$/.test(ans)) {
          correct = ans.toUpperCase();
        } else {
          correct = ans; // store text, resolve later
        }
      } else {
        otherLines.push(line);
      }
    }

    if (optionLines.length >= 4) {
      optionLines.forEach(o => {
        if (o.letter === 'A') optA = o.value;
        else if (o.letter === 'B') optB = o.value;
        else if (o.letter === 'C') optC = o.value;
        else if (o.letter === 'D') optD = o.value;
      });
    }

    // Strategy B: If no labeled options, try comma/semicolon separated on remaining lines
    if (!optA && otherLines.length > 0) {
      // Combine remaining lines and split by comma or semicolon
      const combined = (lines.length === 1 ? lines[0] : otherLines.join(' '));

      // Check for inline format: "question - opt1, opt2, opt3, opt4"
      const dashSplit = questionText.match(/^(.+?)\s*[-–—]\s*(.+)$/);
      if (dashSplit) {
        questionText = dashSplit[1].trim();
        const opts = dashSplit[2].split(/[,;|]/).map(o => o.replace(/\(.*\)/, '').trim()).filter(Boolean);
        if (opts.length >= 4) {
          optA = opts[0]; optB = opts[1]; optC = opts[2]; optD = opts[3];
          // Check for (answer) marker in original
          const answerInParen = dashSplit[2].match(/\(([^)]+)\)/);
          if (answerInParen) correct = answerInParen[1].trim();
        }
      }

      if (!optA) {
        // Try splitting combined other lines by commas/semicolons
        const opts = combined.split(/[,;|]/).map(o => o.replace(/\(.*\)/, '').trim()).filter(Boolean);
        if (opts.length >= 4) {
          optA = opts[0]; optB = opts[1]; optC = opts[2]; optD = opts[3];
          const answerInParen = combined.match(/\(([^)]+)\)/);
          if (answerInParen) correct = answerInParen[1].trim();
        }
      }

      if (!optA && otherLines.length >= 4) {
        // Each line is one option (no labels)
        optA = otherLines[0].replace(/^[-•*]\s*/, '');
        optB = otherLines[1].replace(/^[-•*]\s*/, '');
        optC = otherLines[2].replace(/^[-•*]\s*/, '');
        optD = otherLines[3].replace(/^[-•*]\s*/, '');
        if (otherLines.length >= 5) {
          const lastLine = otherLines[otherLines.length - 1];
          const ans = lastLine.match(/^(?:Correct|Answer|Ans|Right)[\s\-:]+(.+)/i);
          if (ans) correct = ans[1].trim();
        }
      }
    }

    if (!questionText || !optA || !optB || !optC || !optD) return null;

    // Resolve correct answer: if it's text, match to options
    if (correct && correct.length > 1) {
      const cl = correct.toLowerCase();
      if (optA.toLowerCase() === cl || optA.toLowerCase().includes(cl)) correct = 'A';
      else if (optB.toLowerCase() === cl || optB.toLowerCase().includes(cl)) correct = 'B';
      else if (optC.toLowerCase() === cl || optC.toLowerCase().includes(cl)) correct = 'C';
      else if (optD.toLowerCase() === cl || optD.toLowerCase().includes(cl)) correct = 'D';
      else correct = 'A';
    }
    if (!/^[A-D]$/.test(correct)) correct = 'A';

    return { question_text: questionText, option_a: optA, option_b: optB, option_c: optC, option_d: optD, correct_option: correct };
  };

  const tryInlineParse = (lines) => {
    const results = [];
    for (const line of lines) {
      // Format: "Question - opt1, opt2, opt3, opt4 (correct)"
      const dashMatch = line.match(/^(?:\d+[\.\)\-:]\s*)?(.+?)\s*[-–—]\s*(.+)$/);
      if (dashMatch) {
        const qText = dashMatch[1].trim();
        const rest = dashMatch[2];
        const ansInParen = rest.match(/\(([^)]+)\)/);
        const cleanRest = rest.replace(/\([^)]+\)/, '');
        const opts = cleanRest.split(/[,;|]/).map(o => o.trim()).filter(Boolean);
        if (opts.length >= 4) {
          let correct = 'A';
          if (ansInParen) {
            const ans = ansInParen[1].trim().toLowerCase();
            if (opts[0].toLowerCase().includes(ans)) correct = 'A';
            else if (opts[1].toLowerCase().includes(ans)) correct = 'B';
            else if (opts[2].toLowerCase().includes(ans)) correct = 'C';
            else if (opts[3].toLowerCase().includes(ans)) correct = 'D';
          }
          results.push({ question_text: qText, option_a: opts[0], option_b: opts[1], option_c: opts[2], option_d: opts[3], correct_option: correct });
        }
      }
    }
    return results;
  };

  const exportTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/quiz-templates/export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'quiz_templates.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to export templates');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'finalized':
        return <span className="px-2 py-1 rounded-full text-xs font-bold bg-[#dcfce7] text-[#16a34a]">Finalized</span>;
      case 'draft':
        return <span className="px-2 py-1 rounded-full text-xs font-bold bg-[#fef3c7] text-[#d97706]">Draft</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-bold bg-[#f1f5f9] text-[#64748b]">Empty</span>;
    }
  };

  const alertBanner = (
    <>
      {error && (
        <div className="bg-[#fef2f2] border-2 border-[#fecaca] text-[#dc2626] px-4 py-3 rounded-2xl mb-4 text-sm font-medium">
          {error} <button onClick={() => setError('')} className="ml-2 font-bold">×</button>
        </div>
      )}
      {success && (
        <div className="bg-[#dcfce7] border-2 border-[#86efac] text-[#16a34a] px-4 py-3 rounded-2xl mb-4 text-sm font-medium flex items-center gap-2">
          <CheckCircle className="w-4 h-4" /> {success}
        </div>
      )}
    </>
  );

  // ========== BUILDER VIEW ==========
  if (view === 'builder' && activeCategory) {
    const targetCount = activeCategory.questionCount || 10;
    const currentCount = questions.length;
    const countColor = currentCount >= targetCount ? 'text-[#22c55e]' : 'text-[#d97706]';

    return (
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => { setView('categories'); openFolder(activeFolder); }} className="text-sm text-[#2565eb] font-semibold mb-2 hover:underline flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Back to {activeFolder?.name || 'Categories'}
            </button>
            <h1 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {activeCategory.name}
            </h1>
            <p className="text-sm text-[#64748b]">
              Age: {inheritedAgeGroup} | Time: {activeCategory.timeLimit} min
              {activeCategory.status === 'finalized' && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-[#dcfce7] text-[#16a34a]">Finalized</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className={`text-lg font-bold ${countColor}`} data-testid="question-counter">
              {currentCount} / {targetCount}
            </div>
            {isReadOnly && questions.length > 0 && (
              activeCategory.status === 'finalized' ? (
                <button
                  onClick={() => unlockCategory(activeCategory.id, { stayInBuilder: true })}
                  className="px-4 py-2 rounded-xl text-sm font-bold border-2 border-[#d97706] text-[#d97706] hover:bg-[#fffbeb] transition-all flex items-center gap-2"
                  data-testid="unlock-to-edit-btn"
                >
                  <Unlock className="w-4 h-4" /> Unlock to Edit
                </button>
              ) : (
                <button
                  onClick={() => setIsReadOnly(false)}
                  className="px-4 py-2 rounded-xl text-sm font-bold border-2 border-[#2565eb] text-[#2565eb] hover:bg-[#eff6ff] transition-all flex items-center gap-2"
                  data-testid="edit-questions-btn"
                >
                  <Pencil className="w-4 h-4" /> Edit
                </button>
              )
            )}
          </div>
        </div>

        {alertBanner}

        {/* AI Instructions — hidden in read-only mode */}
        {!isReadOnly && (
          <div className="card-olympiad p-6 mb-6">
            <h3 className="text-sm font-bold text-[#475569] uppercase tracking-wide mb-3">AI Instructions</h3>
            <textarea
              value={aiInstructions}
              onChange={(e) => setAiInstructions(e.target.value)}
              className="input-olympiad min-h-[120px] text-sm"
              placeholder="Describe what kind of questions to generate. Include difficulty mix, topics, tone, specific requirements..."
              data-testid="ai-instructions"
            />
            <div className="flex flex-wrap gap-3 mt-3">
              <button
                onClick={generateQuestions}
                disabled={generating || !aiInstructions.trim()}
                className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
                data-testid="generate-questions-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate
              </button>
              <div className="flex items-center gap-2">
                <select
                  onChange={(e) => {
                    const t = templates.find(t => t.id === e.target.value);
                    if (t) loadTemplate(t);
                  }}
                  className="px-3 py-2 rounded-xl border-2 border-[#e2e8f0] text-xs font-medium text-[#475569]"
                  defaultValue=""
                  data-testid="load-template-dropdown"
                >
                  <option value="" disabled>Load Template...</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="px-3 py-2 rounded-xl border-2 border-[#e2e8f0] text-xs w-32"
                  placeholder="Template name"
                  data-testid="template-name-input"
                />
                <button
                  onClick={saveTemplate}
                  disabled={!templateName.trim() || !aiInstructions.trim()}
                  className="px-3 py-2 rounded-xl text-xs font-bold bg-[#f1f5f9] text-[#475569] hover:bg-[#e2e8f0] disabled:opacity-50"
                  data-testid="save-template-btn"
                >
                  Save as Template
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Questions Display */}
        {questions.length > 0 && (
          isReadOnly ? (
            <>
            {/* Finalize button in read-only mode for draft categories */}
            {activeCategory.status !== 'finalized' && questions.length >= (activeCategory.questionCount || 1) && (
              <div className="mb-6">
                {!confirmFinalize ? (
                  <button onClick={() => setConfirmFinalize(true)} disabled={loading} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50" data-testid="finalize-readonly-btn">
                    <Lock className="w-4 h-4" /> Finalize Category
                  </button>
                ) : (
                  <div className="bg-[#fef3c7] border-2 border-[#f59e0b] rounded-2xl px-5 py-4 flex items-center gap-4" data-testid="finalize-confirm-bar">
                    <p className="text-sm font-semibold text-[#92400e] flex-1">Finalize this category? It will be locked and moved to the live question bank.</p>
                    <button onClick={finalizeCategory} disabled={loading} className="px-4 py-2 rounded-xl text-sm font-bold bg-[#16a34a] text-white hover:bg-[#15803d] transition-all flex items-center gap-2 disabled:opacity-50" data-testid="finalize-confirm-yes">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Yes, Finalize
                    </button>
                    <button onClick={() => setConfirmFinalize(false)} className="px-4 py-2 rounded-xl text-sm font-bold text-[#64748b] hover:bg-[#f1f5f9] transition-all">
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-3 mb-6" data-testid="questions-readonly">
              {questions.map((q, i) => (
                <div key={i} className="card-olympiad p-5" data-testid={`readonly-q-${i}`}>
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#eff6ff] flex items-center justify-center text-xs font-bold text-[#2565eb]">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-semibold text-[#0f172a] mb-3 text-sm">{q.question_text}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {['A', 'B', 'C', 'D'].map(opt => {
                          const isCorrect = q.correct_option === opt;
                          return (
                            <div
                              key={opt}
                              className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                                isCorrect
                                  ? 'bg-[#dcfce7] border-2 border-[#22c55e] font-semibold text-[#15803d]'
                                  : 'bg-[#f8fafc] border border-[#e2e8f0] text-[#475569]'
                              }`}
                              data-testid={`readonly-q-${i}-opt-${opt}`}
                            >
                              <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                                isCorrect ? 'bg-[#22c55e] text-white' : 'bg-[#e2e8f0] text-[#64748b]'
                              }`}>{opt}</span>
                              {q[`option_${opt.toLowerCase()}`]}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </>
          ) : (
            /* ===== EDITABLE TABLE ===== */
            <div className="card-olympiad overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="questions-table">
                  <thead>
                    <tr className="bg-[#f8fafc] border-b-2 border-[#e2e8f0]">
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#64748b]">#</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#64748b] min-w-[200px]">Question</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#64748b]">A</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#64748b]">B</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#64748b]">C</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#64748b]">D</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#64748b]">Ans</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#64748b]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#e2e8f0]">
                    {questions.map((q, i) => (
                      <tr key={i} className="hover:bg-[#f8fafc]">
                        <td className="px-3 py-2 text-[#64748b] font-mono">{i + 1}</td>
                        <td className="px-3 py-2">
                          <input type="text" value={q.question_text} onChange={(e) => updateQuestion(i, 'question_text', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-[#e2e8f0] text-xs focus:border-[#2565eb] focus:outline-none" data-testid={`q-${i}-text`} />
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={q.option_a} onChange={(e) => updateQuestion(i, 'option_a', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-[#e2e8f0] text-xs focus:border-[#2565eb] focus:outline-none" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={q.option_b} onChange={(e) => updateQuestion(i, 'option_b', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-[#e2e8f0] text-xs focus:border-[#2565eb] focus:outline-none" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={q.option_c} onChange={(e) => updateQuestion(i, 'option_c', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-[#e2e8f0] text-xs focus:border-[#2565eb] focus:outline-none" />
                        </td>
                        <td className="px-3 py-2">
                          <input type="text" value={q.option_d} onChange={(e) => updateQuestion(i, 'option_d', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-[#e2e8f0] text-xs focus:border-[#2565eb] focus:outline-none" />
                        </td>
                        <td className="px-3 py-2">
                          <select value={q.correct_option} onChange={(e) => updateQuestion(i, 'correct_option', e.target.value)} className="px-2 py-1 rounded-lg border border-[#e2e8f0] text-xs font-bold focus:border-[#2565eb] focus:outline-none" data-testid={`q-${i}-answer`}>
                            <option value="A">A</option><option value="B">B</option><option value="C">C</option><option value="D">D</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex gap-1">
                            <button onClick={() => regenerateQuestion(i)} className="p-1 rounded text-[#2565eb] hover:bg-[#eff6ff]" title="Regenerate" data-testid={`q-${i}-regen`}>
                              <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => deleteQuestion(i)} className="p-1 rounded text-[#ef4444] hover:bg-[#fef2f2]" title="Delete" data-testid={`q-${i}-delete`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}

        {/* Action Buttons — hidden in read-only mode */}
        {!isReadOnly && (
          <>
            {/* Paste Questions Box */}
            {showPasteBox && (
              <div className="card-olympiad p-6 mb-4 animate-fade-in-up" data-testid="paste-questions-box">
                <h3 className="text-sm font-bold text-[#475569] uppercase tracking-wide mb-3">Paste Questions</h3>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  className="input-olympiad min-h-[250px] text-sm font-mono"
                  placeholder={`Paste questions in any format. Examples:\n\n1. What is 5 + 3?\nA) 6\nB) 7\nC) 8\nD) 9\nCorrect: C\n\nOR:\n\nCapital of France - London, Paris, Berlin, Rome (Paris)\n\nOR:\n\nWhat is the largest planet?\nJupiter\nSaturn\nEarth\nMars\nAnswer: Jupiter`}
                  data-testid="paste-questions-textarea"
                />
                {parseError && (
                  <p className="text-sm text-[#ef4444] mt-2">{parseError}</p>
                )}
                <div className="flex gap-3 mt-3">
                  <button
                    onClick={parseAndLoadQuestions}
                    disabled={!pasteText.trim()}
                    className="px-4 py-2.5 rounded-xl font-bold bg-[#2565eb] text-white text-sm flex items-center gap-2 hover:bg-[#1e3a8a] transition-all disabled:opacity-50"
                    data-testid="parse-load-btn"
                  >
                    <CheckCircle className="w-4 h-4" /> Parse & Load
                  </button>
                  <button
                    onClick={() => { setShowPasteBox(false); setPasteText(''); setParseError(''); }}
                    className="px-4 py-2.5 rounded-xl font-bold text-[#64748b] text-sm hover:bg-[#f1f5f9] transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button onClick={() => setShowPasteBox(!showPasteBox)} className="px-4 py-2.5 rounded-xl font-bold border-2 border-[#e2e8f0] text-[#475569] hover:border-[#2565eb] hover:text-[#2565eb] transition-all text-sm flex items-center gap-2" data-testid="paste-questions-btn">
                <ClipboardPaste className="w-4 h-4" /> Paste Questions
              </button>
              <button onClick={addBlankQuestion} className="px-4 py-2.5 rounded-xl font-bold border-2 border-[#e2e8f0] text-[#475569] hover:border-[#2565eb] hover:text-[#2565eb] transition-all text-sm flex items-center gap-2" data-testid="add-question-btn">
                <Plus className="w-4 h-4" /> Add Question
              </button>
              <button onClick={saveQuestions} disabled={loading} className="px-4 py-2.5 rounded-xl font-bold bg-[#2565eb] text-white text-sm flex items-center gap-2 hover:bg-[#1e3a8a] transition-all disabled:opacity-50" data-testid="save-questions-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Save
              </button>
              {questions.length > 0 && (
                <button onClick={finalizeCategory} disabled={loading || questions.length < (activeCategory.questionCount || 1)} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50" data-testid="finalize-category-btn">
                  <Lock className="w-4 h-4" /> Finalize Category
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // ========== CATEGORIES VIEW (inside a folder) ==========
  if (view === 'categories' && activeFolder) {
    return (
      <div className="p-6 md:p-8 max-w-5xl mx-auto">
        <div className="mb-6">
          <button onClick={backToFolders} className="text-sm text-[#2565eb] font-semibold mb-2 hover:underline flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> All Question Folders
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {activeFolder.name}
              </h1>
              <p className="text-sm text-[#64748b] mt-1">
                {categories.filter(c => c.status === 'finalized').length} of 8 categories finalized
              </p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-[#2565eb] text-white hover:bg-[#1e3a8a] transition-all flex items-center gap-1"
              data-testid="create-category-btn"
            >
              <Plus className="w-3.5 h-3.5" /> New Category
            </button>
          </div>
        </div>

        {alertBanner}

        {/* Create Category Form */}
        {showCreateForm && (
          <div className="card-olympiad p-6 mb-6 animate-fade-in-up" data-testid="create-category-form">
            <h3 className="text-base font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>Create Category</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-[#475569] mb-1.5">Category Name</label>
                <input type="text" value={newCategory.name} onChange={(e) => setNewCategory(c => ({ ...c, name: e.target.value }))} className="input-olympiad" placeholder="e.g. Mathematics" data-testid="category-name-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#475569] mb-1.5">Number of Questions</label>
                <input type="number" value={newCategory.questionCount} onChange={(e) => setNewCategory(c => ({ ...c, questionCount: parseInt(e.target.value) || 10 }))} className="input-olympiad" min="1" max="50" data-testid="category-count-input" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#475569] mb-1.5">Time Limit (minutes)</label>
                <input type="number" value={newCategory.timeLimit} onChange={(e) => setNewCategory(c => ({ ...c, timeLimit: parseInt(e.target.value) || 7 }))} className="input-olympiad" min="1" max="30" data-testid="category-time-input" />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={createCategory} disabled={loading || !newCategory.name.trim()} className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50" data-testid="submit-create-category">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
              </button>
              <button onClick={() => setShowCreateForm(false)} className="px-5 py-2.5 rounded-xl text-sm font-bold text-[#64748b] hover:bg-[#f1f5f9] transition-all">Cancel</button>
            </div>
          </div>
        )}

        {/* Categories Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#2565eb] animate-spin" />
          </div>
        ) : categories.length === 0 ? (
          <div className="card-olympiad p-12 text-center">
            <BookOpen className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
            <p className="text-lg font-semibold text-[#64748b]">No categories yet</p>
            <p className="text-sm text-[#94a3b8]">Create your first category to start building questions for this competition.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="categories-grid">
            {categories.map((cat) => {
              const catMenu = catMenuOpen === cat.id;
              const isRenamingThis = renamingCat === cat.id;
              return (
              <div key={cat.id} className="card-olympiad p-5 hover:border-[#2565eb] cursor-pointer transition-all relative" onClick={() => !isRenamingThis && openBuilder(cat)}>
                <div className="flex items-start justify-between mb-2">
                  {isRenamingThis ? (
                    <div className="flex-1 mr-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={catRenameValue}
                        onChange={(e) => setCatRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') renameCategory(cat.id); if (e.key === 'Escape') setRenamingCat(null); }}
                        className="input-olympiad text-sm py-1.5 mb-2"
                        autoFocus
                        data-testid={`cat-rename-input-${cat.id}`}
                      />
                      <div className="flex gap-2">
                        <button onClick={() => renameCategory(cat.id)} className="px-3 py-1 rounded-lg text-xs font-bold bg-[#2565eb] text-white" data-testid={`cat-rename-save-${cat.id}`}>Save</button>
                        <button onClick={() => setRenamingCat(null)} className="px-3 py-1 rounded-lg text-xs font-bold text-[#64748b] hover:bg-[#f1f5f9]">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <h3 className="font-bold text-[#0f172a]">{cat.name}</h3>
                  )}
                  <div className="flex items-center gap-2">
                    {getStatusBadge(cat.status)}
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setCatMenuOpen(catMenu ? null : cat.id); }}
                        className="p-1 rounded-lg text-[#94a3b8] hover:text-[#475569] hover:bg-[#f1f5f9] transition-all"
                        data-testid={`cat-menu-${cat.id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {catMenu && (
                        <div className="absolute right-0 top-8 z-20 bg-white rounded-xl border-2 border-[#e2e8f0] shadow-lg py-1 w-36 animate-fade-in-up" data-testid={`cat-dropdown-${cat.id}`}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setCatMenuOpen(null); setRenamingCat(cat.id); setCatRenameValue(cat.name); }}
                            className="w-full px-4 py-2 text-left text-sm font-medium text-[#475569] hover:bg-[#f8fafc] flex items-center gap-2"
                            data-testid={`cat-rename-${cat.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" /> Rename
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setCatMenuOpen(null); deleteCategory(cat); }}
                            className="w-full px-4 py-2 text-left text-sm font-medium text-[#ef4444] hover:bg-[#fef2f2] flex items-center gap-2"
                            data-testid={`cat-delete-${cat.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {!isRenamingThis && (
                  <>
                    <p className="text-xs text-[#64748b] mb-3">
                      {cat.questionCount} questions | {cat.timeLimit} min | Ages {inheritedAgeGroup}
                    </p>
                    <div className="flex items-center gap-2">
                      {cat.status === 'finalized' ? (
                        <span className="text-xs font-bold text-[#16a34a] flex items-center gap-1" data-testid={`view-${cat.id}`}>
                          <Lock className="w-3 h-3" /> Click to View
                        </span>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); openBuilder(cat); }} className="text-xs font-bold text-[#2565eb] flex items-center gap-1 hover:underline" data-testid={`edit-${cat.id}`}>
                          <Pencil className="w-3 h-3" /> {cat.status === 'empty' ? 'Start Building' : 'Edit Questions'}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ========== FOLDERS VIEW (top level) ==========
  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Questions
          </h1>
          <p className="text-sm text-[#64748b] mt-1">Question banks organized by competition. Click a folder to manage its categories.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowNewFolderForm(true); setNewFolderName(''); }}
            className="px-4 py-2 rounded-xl text-xs font-bold bg-[#2565eb] text-white hover:bg-[#1e40af] transition-all flex items-center gap-1"
            data-testid="open-create-folder-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New Folder
          </button>
          <button
            onClick={exportTemplates}
            className="px-4 py-2 rounded-xl text-xs font-bold border-2 border-[#e2e8f0] text-[#475569] hover:border-[#2565eb] hover:text-[#2565eb] transition-all flex items-center gap-1"
            data-testid="export-templates-btn"
          >
            <Download className="w-3.5 h-3.5" /> Templates
          </button>
        </div>
      </div>

      {alertBanner}

      {/* Create-Folder Inline Form */}
      {showNewFolderForm && (
        <div className="card-olympiad p-4 mb-6 animate-fade-in-up" data-testid="create-folder-form">
          <h3 className="text-sm font-bold text-[#0f172a] mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Create a new question folder
          </h3>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              autoFocus
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') setShowNewFolderForm(false); }}
              placeholder="e.g. Mock Test V2 — Grade 4"
              className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[#e2e8f0] focus:border-[#2565eb] outline-none text-sm"
              maxLength={100}
              data-testid="new-folder-name-input"
            />
            <button
              onClick={createFolder}
              disabled={!newFolderName.trim() || creatingFolder}
              className="px-5 py-2.5 rounded-xl text-sm font-bold bg-[#16a34a] text-white hover:bg-[#15803d] transition-all flex items-center gap-2 disabled:opacity-50"
              data-testid="confirm-create-folder-btn"
            >
              {creatingFolder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create
            </button>
            <button
              onClick={() => { setShowNewFolderForm(false); setNewFolderName(''); }}
              className="px-5 py-2.5 rounded-xl text-sm font-bold text-[#64748b] hover:bg-[#f1f5f9] transition-all"
              data-testid="cancel-create-folder-btn"
            >
              Cancel
            </button>
          </div>
          <p className="text-xs text-[#94a3b8] mt-2">
            Folders created here are independent — you can link them to any competition later from the Competition Setup page.
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#2565eb] animate-spin" />
        </div>
      ) : folders.length === 0 ? (
        <div className="card-olympiad p-12 text-center" data-testid="empty-folders-state">
          <FolderOpen className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
          <p className="text-lg font-semibold text-[#64748b]">No question folders yet</p>
          <p className="text-sm text-[#94a3b8] mb-5">Create one now, or set up a competition to auto-generate a folder.</p>
          <button
            onClick={() => { setShowNewFolderForm(true); setNewFolderName(''); }}
            className="px-5 py-2.5 rounded-xl text-sm font-bold bg-[#2565eb] text-white hover:bg-[#1e40af] transition-all inline-flex items-center gap-2"
            data-testid="empty-state-create-folder-btn"
          >
            <Plus className="w-4 h-4" /> Create your first folder
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="folders-grid">
          {folders.map((folder) => {
            const isReady = folder.status === 'ready';
            const menuOpen = folderMenuOpen === folder.id;
            const isRenaming = renamingFolder === folder.id;
            return (
              <div
                key={folder.id}
                onClick={() => !isRenaming && openFolder(folder)}
                className="card-olympiad p-5 hover:border-[#2565eb] cursor-pointer transition-all group relative"
                data-testid={`folder-${folder.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#eff6ff] flex items-center justify-center group-hover:bg-[#2565eb] transition-colors">
                    <FolderOpen className="w-5 h-5 text-[#2565eb] group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                      isReady
                        ? 'bg-[#dcfce7] text-[#16a34a]'
                        : 'bg-[#f1f5f9] text-[#64748b]'
                    }`} data-testid={`folder-status-${folder.id}`}>
                      {isReady ? 'Ready' : 'Incomplete'}
                    </span>
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setFolderMenuOpen(menuOpen ? null : folder.id); }}
                        className="p-1 rounded-lg text-[#94a3b8] hover:text-[#475569] hover:bg-[#f1f5f9] transition-all"
                        data-testid={`folder-menu-${folder.id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {menuOpen && (
                        <div className="absolute right-0 top-8 z-20 bg-white rounded-xl border-2 border-[#e2e8f0] shadow-lg py-1 w-36 animate-fade-in-up" data-testid={`folder-dropdown-${folder.id}`}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFolderMenuOpen(null);
                              setRenamingFolder(folder.id);
                              setRenameValue(folder.name);
                            }}
                            className="w-full px-4 py-2 text-left text-sm font-medium text-[#475569] hover:bg-[#f8fafc] flex items-center gap-2"
                            data-testid={`folder-rename-${folder.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" /> Rename
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFolderMenuOpen(null);
                              deleteFolder(folder);
                            }}
                            className="w-full px-4 py-2 text-left text-sm font-medium text-[#ef4444] hover:bg-[#fef2f2] flex items-center gap-2"
                            data-testid={`folder-delete-${folder.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {isRenaming ? (
                  <div className="mb-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') renameFolder(folder.id); if (e.key === 'Escape') setRenamingFolder(null); }}
                      className="input-olympiad text-sm py-2 mb-2"
                      autoFocus
                      data-testid={`folder-rename-input-${folder.id}`}
                    />
                    <div className="flex gap-2">
                      <button onClick={() => renameFolder(folder.id)} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#2565eb] text-white hover:bg-[#1e3a8a] transition-all" data-testid={`folder-rename-save-${folder.id}`}>Save</button>
                      <button onClick={() => setRenamingFolder(null)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#64748b] hover:bg-[#f1f5f9] transition-all">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="font-bold text-[#0f172a] mb-1">{folder.name}</h3>
                    <p className="text-sm text-[#64748b]">
                      {folder.finalized_categories} of 8 categories complete
                    </p>
                    <div className="mt-3 w-full bg-[#e2e8f0] rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all ${isReady ? 'bg-[#22c55e]' : 'bg-[#2565eb]'}`}
                        style={{ width: `${Math.min(100, (folder.finalized_categories / 8) * 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-end mt-3 text-xs font-semibold text-[#2565eb] opacity-0 group-hover:opacity-100 transition-opacity">
                      Open <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminQuestionsPage;
